import React, { useState, useEffect } from "react";
import Axios from "axios";
import CustomSelect from "../Body/CustomChartComponents/CustomSelect/CustomSelect";
import CustomCheckBox from "../Body/CustomChartComponents/CustomCheckBox/CustomCheckBox";
import CustomIconButton from "../Body/CustomChartComponents/CustomIconButton/CustomIconButton";
import { Cross } from "../CustomComponents/SwiftIcon/Icons";
import { GetCookie } from "../../exports/CookieManagement";
import { Alert } from "../Body/CustomChartComponents/CustomAlert/CustomAlert";
import { COLOR_VARS } from "../../exports/ColorVars";
import { getDeviceSize } from "../../exports/InteractiveUtils";

const REQUEST_BASE_URL = process.env.REACT_APP_REQUEST_BASE_URL;
const DEVICE_SIZE = getDeviceSize();

const report_types = ["Factsheet Statement", "Gain & Loss Statement"];
const report_frequencies = ["Daily", "Weekly", "Monthly"];
const report_targets = ["Individual Customer", "Family Portfolio"];

const VERTICAL_OPTIONS = [
  { key: "stocks", label: "Stocks", requires: ["equity"], category: ["stocks"] },
  { key: "mf", label: "Mutual Fund", requires: ["mutual_funds"], category: ["mutual fund"] },
  { key: "combined", label: "Combined", requires: ["equity", "mutual_funds"], category: ["mutual fund", "stocks"] },
];

const typeToApi = (t) => (t === "Factsheet Statement" ? "factsheet" : "gainloss");
const freqToApi = (f) => (f === "Daily" ? "daily" : f === "Weekly" ? "weekly" : "monthly");

function notify(message, tone = "success") {
  Alert({
    TitleText: "Report Schedule",
    Message: message,
    BandColor: tone === "success" ? "var(--main-blue)" : "var(--red)",
    AutoClose: {
      Active: true,
      Line: true,
      LineColor: tone === "success" ? "var(--main-blue)" : "var(--red)",
      Time: 3,
    },
  });
}

function ReportScheduler({
  customers = [],
  portfolios = [],
  currentUserCode,
  setScheduler,
  setScheduled,
}) {
  const account_token = GetCookie("token");

  const [reportTarget, setReportTarget] = useState(report_targets[0]);
  const [reportType, setReportType] = useState(report_types[0]);
  const [reportFrequency, setReportFrequency] = useState(report_frequencies[0]);
  const [verticalLabel, setVerticalLabel] = useState("");
  const [verticalsMap, setVerticalsMap] = useState({});
  const [existing, setExisting] = useState([]);
  const [checked, setChecked] = useState({});
  const [sendToSelf, setSendToSelf] = useState({});
  const [sendToClient, setSendToClient] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const isFamily = reportTarget === "Family Portfolio";

  useEffect(() => {
    Axios.get(`${REQUEST_BASE_URL}/reports/customer-verticals?token=${account_token}`)
      .then((response) => {
        const data = response.data;
        if (data) setVerticalsMap(data.verticals || {});
      })
      .catch(() => {});

    Axios.get(`${REQUEST_BASE_URL}/reports/schedule?token=${account_token}`)
      .then((response) => {
        const data = response.data;
        setExisting(Array.isArray(data) ? data : data?.data || []);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const type = typeToApi(reportType);
  const freq = freqToApi(reportFrequency);
  const selectedOpt =
    VERTICAL_OPTIONS.find((o) => o.label === verticalLabel) || VERTICAL_OPTIONS[0];

  // A customer's own verticals; a family's union across its members.
  const activeVerticalsFor = (id) => {
    if (!isFamily) return verticalsMap[id] || [];
    const family = portfolios.find((f) => f.family_portfolio_id === id);
    const active = new Set();
    (family?.family_portfolio_customers || []).forEach((m) =>
      (verticalsMap[m.customer_account_code] || []).forEach((v) => active.add(v))
    );
    return Array.from(active);
  };

  // Rows eligible under the current vertical: customers, or families.
  const eligible = isFamily
    ? portfolios.filter((f) =>
        selectedOpt.requires.every((v) => activeVerticalsFor(f.family_portfolio_id).includes(v))
      )
    : customers.filter((c) =>
        selectedOpt.requires.every((v) => activeVerticalsFor(c.account_code).includes(v))
      );

  const rowId = (row) => (isFamily ? row.family_portfolio_id : row.account_code);
  const rowLabel = (row) => (isFamily ? row.name : row.name || row.account_code);

  const availableVerticalOptions = React.useMemo(() => {
    const pool = isFamily
      ? portfolios.map((f) => f.family_portfolio_id)
      : customers.map((c) => c.account_code);

    const hasStocks = pool.some((id) => activeVerticalsFor(id).includes("equity"));
    const hasMF = pool.some((id) => activeVerticalsFor(id).includes("mutual_funds"));
    const hasBoth = pool.some((id) => {
      const v = activeVerticalsFor(id);
      return v.includes("equity") && v.includes("mutual_funds");
    });

    const options = [];
    if (hasStocks) options.push(VERTICAL_OPTIONS.find((o) => o.key === "stocks"));
    if (hasMF) options.push(VERTICAL_OPTIONS.find((o) => o.key === "mf"));
    if (hasBoth) options.push(VERTICAL_OPTIONS.find((o) => o.key === "combined"));
    return options.filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, portfolios, verticalsMap, isFamily]);

  // Existing active schedule for a row under the current context.
  // Family schedules are self-owned only -- there's no single "client" email
  // for a family the way there is for an individual customer.
  const findExisting = (id, ownerType) => {
    const cat = JSON.stringify(selectedOpt.category);

    return existing.find((s) => {
      const owner = ownerType === "client" ? id : currentUserCode;
      const matchTarget = isFamily
        ? s.report_target === "family" && s.portfolio_id === id
        : s.report_target === "customer" && s.account_code === id;

      return (
        matchTarget &&
        s.report_owner === owner &&
        s.report_type === type &&
        s.report_frequency === freq &&
        s.report_status === "active" &&
        (s.report_category || "") === cat
      );
    });
  };

  useEffect(() => {
    const checkedSeed = {};
    const selfSeed = {};
    const clientSeed = {};

    eligible.forEach((row) => {
      const id = rowId(row);

      const existingSelf = findExisting(id, "self");
      const existingClient = !isFamily && findExisting(id, "client");

      const hasSelf = !!existingSelf;
      const hasClient = !!existingClient;

      checkedSeed[id] = hasSelf || hasClient;
      selfSeed[id] = hasSelf;
      clientSeed[id] = hasClient;
    });

    setChecked(checkedSeed);
    setSendToSelf(selfSeed);
    setSendToClient(clientSeed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verticalLabel, type, freq, existing, verticalsMap, portfolios, customers.length, currentUserCode, isFamily]);

  useEffect(() => {
    if (availableVerticalOptions.length === 0) {
      setVerticalLabel("");
      return;
    }

    const currentExists = availableVerticalOptions.some((option) => option.label === verticalLabel);

    if (!currentExists) {
      setVerticalLabel(availableVerticalOptions[0].label);
    }
  }, [availableVerticalOptions, verticalLabel]);

  // Switching target clears the selection so a stale row from the other
  // target can't leak into the request.
  function changeTarget(value) {
    setReportTarget(value);
    setChecked({});
    setSendToSelf({});
    setSendToClient({});
  }

  const allSelected = eligible.length > 0 && eligible.every((row) => checked[rowId(row)]);

  const toggleAll = (val) => {
    const nextChecked = {};
    const nextSelf = {};
    const nextClient = {};

    eligible.forEach((row) => {
      const id = rowId(row);
      nextChecked[id] = val;
      nextSelf[id] = val;
      nextClient[id] = false;
    });

    setChecked(nextChecked);
    setSendToSelf(nextSelf);
    setSendToClient(nextClient);
  };

  function closeScheduler() {
    setScheduler(false);
  }

  async function submit() {
    const createEntries = [];
    const deleteIds = [];

    eligible.forEach((row) => {
      const id = rowId(row);

      const wantSelf = !!sendToSelf[id];
      const wantClient = !isFamily && !!sendToClient[id];
      const existingSelf = findExisting(id, "self");
      const existingClient = !isFamily && findExisting(id, "client");

      if (wantSelf && !existingSelf) {
        createEntries.push(
          isFamily
            ? {
                report_target: "family",
                portfolio_id: id,
                account_code: "",
                report_category: selectedOpt.category,
                report_owner_type: "self",
              }
            : {
                report_target: "customer",
                account_code: id,
                portfolio_id: "",
                report_category: selectedOpt.category,
                report_owner_type: "self",
              }
        );
      }

      if (wantClient && !existingClient) {
        createEntries.push({
          report_target: "customer",
          account_code: id,
          portfolio_id: "",
          report_category: selectedOpt.category,
          report_owner_type: "client",
        });
      }

      if (!wantSelf && existingSelf) deleteIds.push(existingSelf.report_id);
      if (!wantClient && existingClient) deleteIds.push(existingClient.report_id);
    });

    if (createEntries.length === 0 && deleteIds.length === 0) {
      notify("No changes to save.", "warning");
      return;
    }

    setSubmitting(true);
    try {
      if (createEntries.length) {
        await Axios.post(`${REQUEST_BASE_URL}/reports/schedule?token=${account_token}`, {
          report_type: type,
          report_frequency: freq,
          entries: createEntries,
        });
      }

      if (deleteIds.length) {
        await Axios.delete(`${REQUEST_BASE_URL}/reports/schedule`, {
          params: { token: account_token },
          data: { report_ids: deleteIds },
        });
      }

      notify(`Saved — ${createEntries.length} added, ${deleteIds.length} removed.`);
      setScheduled(Math.floor(Math.random() * 100000));
      setScheduler(false);
    } catch (err) {
      console.log(err);
      notify("Something went wrong while saving.", "warning");
    } finally {
      setSubmitting(false);
    }
  }

  const size = getDeviceSize();

  return (
    <>
      <div className="report-scheduler-header">
        <p className="report-scheduler-title">Report Scheduler</p>
        <div className="report-scheduler-close" onClick={closeScheduler}>
          <Cross
            size={DEVICE_SIZE === "S" ? 26 : 24}
            color={COLOR_VARS["SWIFT_COLOR4"]}
          />
        </div>
      </div>

      <div className="report-scheduler-content">
        <div className="report-scheduler-wrapper">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
            }}
          >
            <CustomSelect
              heading="Target"
              defaultIndex={0}
              value={reportTarget}
              onTypeChange={changeTarget}
              options={report_targets}
            />
            <CustomSelect
              heading="Category"
              defaultIndex={0}
              value={verticalLabel}
              onTypeChange={setVerticalLabel}
              options={availableVerticalOptions.map((o) => o.label)}
            />
            <CustomSelect
              heading="Type of Statement"
              defaultIndex={0}
              value={reportType}
              onTypeChange={setReportType}
              options={report_types}
            />
            <CustomSelect
              heading="Frequency"
              defaultIndex={0}
              value={reportFrequency}
              onTypeChange={setReportFrequency}
              options={report_frequencies}
            />
          </div>

          {/* Rows eligible for the selected vertical */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              border: "1px solid var(--border-color)",
              borderRadius: "5px",
              overflow: "hidden",
              marginTop: "16px",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isFamily
                  ? "40px 1fr 80px"
                  : "40px 1fr 1fr 80px 80px",
                alignItems: "center",
                background: "var(--base-200)",
                fontSize: 11,
                textTransform: "uppercase",
                fontWeight: 700,
                color: "var(--font-secondary)",
                borderBottom: "1px solid var(--border-color)",
                padding: "8px 12px",
              }}
            >
              <div>
                <CustomCheckBox
                  width={16}
                  height={16}
                  isChecked={allSelected}
                  onChangeValue={toggleAll}
                />
              </div>

              <div>{isFamily ? "Family" : "Customer"}</div>
              {!isFamily && <div style={{ textAlign: "center" }}>Self</div>}
              <div style={{ textAlign: "center" }}>{isFamily ? "Scheduled" : "Client"}</div>
            </div>

            <div style={{ flex: 1, overflowY: "auto" }}>
              {eligible.length === 0 ? (
                <div
                  style={{
                    padding: 16,
                    fontSize: 12,
                    color: "var(--font-secondary)",
                    textAlign: "center",
                  }}
                >
                  No {isFamily ? "family portfolio" : "customer"} has the {selectedOpt.label} vertical active.
                </div>
              ) : (
                eligible.map((row) => {
                  const id = rowId(row);
                  return (
                    <div
                      key={id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: isFamily
                          ? "40px 1fr 80px"
                          : "40px 1fr 1fr 80px 80px",
                        alignItems: "center",
                        padding: "8px 12px",
                        fontSize: 12,
                        borderBottom: "1px solid var(--border-color)",
                      }}
                    >
                      {/* Row selection */}
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <CustomCheckBox
                          width={16}
                          height={16}
                          isChecked={!!checked[id]}
                          onChangeValue={(val) => {
                            setChecked((prev) => ({ ...prev, [id]: val }));
                            // Selecting defaults to Self.
                            setSendToSelf((prev) => ({ ...prev, [id]: val }));
                            // Unselecting turns Client off (individual only).
                            if (!val && !isFamily) {
                              setSendToClient((prev) => ({ ...prev, [id]: false }));
                            }
                          }}
                        />
                      </div>

                      {/* Label */}
                      <div
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {rowLabel(row)}
                      </div>

                      {/* Self (individual only -- family has just the one column) */}
                      {!isFamily && (
                        <div style={{ display: "flex", justifyContent: "center" }}>
                          <CustomCheckBox
                            width={16}
                            height={16}
                            isChecked={!!sendToSelf[id]}
                            onChangeValue={(value) => {
                              setSendToSelf((prev) => ({ ...prev, [id]: value }));
                              if (!value && !sendToClient[id]) {
                                setChecked((prev) => ({ ...prev, [id]: false }));
                              }
                              if (value) {
                                setChecked((prev) => ({ ...prev, [id]: true }));
                              }
                            }}
                          />
                        </div>
                      )}

                      {/* Client (individual) / Scheduled (family) */}
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        {isFamily ? (
                          <CustomCheckBox
                            width={16}
                            height={16}
                            isChecked={!!sendToSelf[id]}
                            onChangeValue={(value) => {
                              setSendToSelf((prev) => ({ ...prev, [id]: value }));
                              setChecked((prev) => ({ ...prev, [id]: value }));
                            }}
                          />
                        ) : (
                          <CustomCheckBox
                            width={16}
                            height={16}
                            isChecked={!!sendToClient[id]}
                            onChangeValue={(value) => {
                              setSendToClient((prev) => ({ ...prev, [id]: value }));
                              if (value) {
                                setChecked((prev) => ({ ...prev, [id]: true }));
                              }
                              if (!value && !sendToSelf[id]) {
                                setChecked((prev) => ({ ...prev, [id]: false }));
                              }
                            }}
                          />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="report-section-button" style={{ marginTop: 16 }}>
            <CustomIconButton
              className={size === "S" ? "btn-xl" : ""}
              onClick={submit}
              icon={null}
              title={submitting ? "Saving..." : "Save Reports"}
              disabled={submitting || eligible.length === 0}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default ReportScheduler;
